import { createElement } from 'react';
import { Navigate, Route, useLocation, useOutletContext, useParams } from 'react-router-dom';
import { AUDIENCE } from '../lib/lifecycle-notice.js';
import { CUSTOMER_SECTIONS, canSee, customerScope, customerSectionPath, readsOverviewRecord, reviewsOnly, sectionLabel, sectionTarget, viewerOnly } from '../lib/navigation.js';
import LifecycleNotice from '../components/LifecycleNotice.jsx';
import PageHeader from '../components/PageHeader.jsx';
import PageState from '../components/PageState.jsx';
import OverviewPage from './overview/OverviewPage.jsx';
import SecurityPage from './security/SecurityPage.jsx';
import LicensesPage from './licenses/LicensesPage.jsx';
import AccessReviewsPage from './access-reviews/AccessReviewsPage.jsx';
import MyReviewsPage from './access-reviews/MyReviewsPage.jsx';
import { staffReadOnly } from '../lib/staff-act.js';
import DataCollectionPage from './data-collection/DataCollectionPage.jsx';
import CustomerQlik from './CustomerQlik.jsx';
import NotificationsSection from './notifications/NotificationsSection.jsx';
import WatchEventPage from './activity/WatchEventPage.jsx';
import ActivityPage from './activity/ActivityPage.jsx';
import ComplianceSection from './compliance/ComplianceSection.jsx';
import CustomerPeople from './CustomerPeople.jsx';
import SettingsPage from './settings/SettingsPage.jsx';
import DataReference from './DataReference.jsx';
import ImplementationFootprint from './implementation/ImplementationFootprint.jsx';
import { LegacyComplianceRedirect, LegacyCustomerRedirect, LegacySectionRedirect } from './LegacyRedirect.jsx';
import { LEGACY_SECTIONS, legacySettingsSectionTarget } from '../lib/legacy-routes.js';
import { operatorWithoutAdmin, readsWatchContent, watchOperatorAccount } from '../lib/tenant-access.js';
import { activityPath, notificationsPath } from '../lib/watch-format.js';
import { complianceRole } from '../lib/compliance-role.js';
import { generateReportPath } from '../lib/report-types.js';

// One route element per customer section (portal workbench, 2026-09-29),
// rendered inside the customer layout (pages/CustomerDetail.jsx). Each moves
// today's page in as it is. A user who cannot see a section is redirected
// with replace to the first section they can use (lib/navigation.js
// sectionTarget); the API still refuses them on its own either way.
//
// ENTRAID-347 redesign: every section page owns exactly one h1, its PageHeader (the customer's name lives in the top
// bar switcher, no longer in a header on every page). Until each page is rebuilt with its own header, the wrapper
// renders a title-only PageHeader from the sidebar label; a page that draws its own passes ownHeader.

function useSectionAccess(id) {
  const ctx = useOutletContext();
  const allowed = canSee(ctx.navClaims, customerScope(ctx.customerId), id);
  return { ctx, redirect: allowed ? null : sectionTarget(ctx.navClaims, ctx.customerId, id) };
}

/** The title-only page header of a section that does not draw its own yet. */
// The one-line subline of a section that draws no header of its own (ENTRAID-347 design language: say what the page is
// for in one line).
const SECTION_SUBLINES = Object.freeze({
  reports: 'Reports for your auditors, the controls behind them and your attestations.',
  'qlik-cloud': 'The Qlik Cloud connection that loads your onGuard data into Qlik apps.',
  people: 'Who can sign in to onGuard for this tenant, and with which role.',
});

function SectionHeader({ id }) {
  return <PageHeader title={sectionLabel(id)} subline={SECTION_SUBLINES[id] ?? null} />;
}

/** The record's loading and error states, under the section's page header. */
function RecordPending({ id, error }) {
  if (error) return <><SectionHeader id={id} /><PageState state="error" title="The customer could not be loaded" message={error} /></>;
  return <><SectionHeader id={id} /><PageState state="loading" header={false} cards={id === 'overview' ? 4 : 0} label={`Loading ${sectionLabel(id)}`} /></>;
}

/** A section that loads its own data. */
function PlainSection({ id, render, ownHeader = false }) {
  const { ctx, redirect } = useSectionAccess(id);
  if (redirect) return <Navigate to={redirect} replace />;
  const owns = typeof ownHeader === 'function' ? ownHeader(ctx) : ownHeader;
  return <>{owns ? null : <SectionHeader id={id} />}{render(ctx)}</>;
}

/** A section built on the customer record the layout loaded. */
function RecordSection({ id, render, ownHeader = false }) {
  const { ctx, redirect } = useSectionAccess(id);
  if (redirect) return <Navigate to={redirect} replace />;
  if (ctx.recordError || !ctx.record) return <RecordPending id={id} error={ctx.recordError} />;
  const isOperator = ctx.navClaims.isOperator === true;
  const page = render({
    ...ctx.record,
    customerId: ctx.customerId,
    isOperator,
    audience: isOperator ? AUDIENCE.OPERATOR : AUDIENCE.CUSTOMER,
    refresh: ctx.refresh,
    // The viewer role: read-only Overview and Security (lib/navigation.js viewerOnly).
    readOnly: viewerOnly(ctx.navClaims, ctx.customerId),
  });
  return <>{ownHeader ? null : <SectionHeader id={id} />}{page}</>;
}

/** The customer's own admin, never an operator account (also not in "View as client"): only they read Watch content. */
function readsWatch(ctx) {
  return readsWatchContent(ctx?.me, ctx?.navClaims, ctx?.customerId);
}

export function OverviewSection() {
  const ctx = useOutletContext();
  const role = complianceRole(ctx?.me, ctx?.navClaims, ctx?.customerId);
  return (
    <RecordSection
      id="overview"
      ownHeader
      render={(r) => (
        <OverviewPage
          customer={r.customer} config={r.config} recentRuns={r.recentRuns} audience={r.audience} onRefresh={r.refresh}
          complianceRole={role} marketplaceListingUrl={ctx?.me?.config?.marketplaceListingUrl ?? ''}
          watchAllowed={readsWatch(ctx)}
          historyAllowed={readsOverviewRecord(ctx?.navClaims, ctx?.customerId)}
          readOnly={r.readOnly}
        />
      )}
    />
  );
}

/** Licenses (License Insights, 2026-10-26): every tier; the viewer reads aggregates only, the API decides names. */
export function LicensesSection() {
  const ctx = useOutletContext();
  return (
    <RecordSection
      id="licenses"
      ownHeader
      render={(r) => (
        <LicensesPage
          customerId={r.customerId} customer={r.customer} audience={r.audience} onRefresh={r.refresh}
          readOnly={r.readOnly} staffReadOnly={staffReadOnly(ctx?.me, r.customer)}
        />
      )}
    />
  );
}

/**
 * Access reviews (spec 2026-10-26): the page, or "Your review" at access-reviews/mine for a signed-in reviewer. The
 * viewer and the operator read counts only; the API decides names and actions.
 */
export function AccessReviewsSection() {
  const ctx = useOutletContext();
  const rest = useParams()['*'] ?? '';
  // The reviewer role alone (spec 2026-10-29): "Your reviews" is all it opens, and it needs no customer record.
  if (reviewsOnly(ctx?.navClaims, ctx?.customerId)) {
    if (rest !== 'mine') return <Navigate to={`${customerSectionPath(ctx.customerId, 'access-reviews')}/mine`} replace />;
    return <PlainSection id="access-reviews" ownHeader render={(c) => <MyReviewsPage customerId={c.customerId} showBack={false} />} />;
  }
  return (
    <RecordSection
      id="access-reviews"
      ownHeader
      render={(r) => (rest === 'mine' ? <MyReviewsPage customerId={r.customerId} /> : (
        <AccessReviewsPage
          customerId={r.customerId} customer={r.customer} audience={r.audience} onRefresh={r.refresh}
          readOnly={r.readOnly} staffReadOnly={staffReadOnly(ctx?.me, r.customer)}
          marketplaceListingUrl={ctx?.me?.config?.marketplaceListingUrl ?? ''}
        />
      ))}
    />
  );
}

export function SecuritySection() {
  const ctx = useOutletContext();
  // "Create report" (plan C2b) is for the account that may generate reports here: the customer's admin (an operator
  // who holds the admin role here included). Security posture is in every tier.
  const role = complianceRole(ctx?.me, ctx?.navClaims, ctx?.customerId);
  const createReportTo = role === 'admin'
    ? generateReportPath(sectionTarget(ctx?.navClaims, ctx?.customerId, 'reports'), 'security-posture')
    : null;
  // An operator account without the admin role here exports a client-side summary instead (Hugo, 2026-10-21): the
  // customer's report function never builds a report for an operator.
  const operatorExport = role === 'operator';
  return (
    <RecordSection
      id="security"
      ownHeader
      render={(r) => (
        <SecurityPage
          securityPosture={r.recentRuns?.[0]?.securityPosture ?? null}
          runTimestamp={r.recentRuns?.[0]?.runTimestamp ?? null}
          runs={r.recentRuns}
          customer={r.customer}
          audience={r.audience}
          onRefresh={r.refresh}
          historyAllowed={readsOverviewRecord(ctx?.navClaims, ctx?.customerId)}
          createReportTo={createReportTo}
          operatorExport={operatorExport}
          readOnly={r.readOnly}
        />
      )}
    />
  );
}

/** Data collection (ENTRAID-347 redesign, plan C5): Sources, Collections and the schedule on one page. */
export function DataCollectionSection() {
  return (
    <RecordSection
      id="data-collection"
      ownHeader
      render={(r) => (
        <DataCollectionPage customer={r.customer} config={r.config} recentRuns={r.recentRuns} audience={r.audience} onRefresh={r.refresh} />
      )}
    />
  );
}

export function QlikCloudSection() {
  return (
    <PlainSection
      id="qlik-cloud"
      render={(ctx) => <CustomerQlik customerId={ctx.customerId} isOperator={ctx.navClaims.isOperator === true} marketplaceListingUrl={ctx.me?.config?.marketplaceListingUrl ?? ''} />}
    />
  );
}

/**
 * Activity (ENTRAID-347 redesign, plan C3): the Watch events and the flagged cases, moved out of Notifications. Same
 * access as Notifications had (operator or the customer's admin); an operator account gets Watch health only (the
 * account's own operator flag, unless it holds onguard-entraid-admin on this customer, ENTRAID-302).
 */
export function ActivitySection() {
  const me = useOutletContext()?.me;
  return (
    <RecordSection
      id="activity"
      ownHeader
      render={(r) => (
        <div className="space-y-4">
          <LifecycleNotice customer={r.customer} audience={r.audience} hasData onRefresh={r.refresh} />
          <ActivityPage customerId={r.customerId} operatorAccount={watchOperatorAccount(me, r.customerId)} />
        </div>
      )}
    />
  );
}

export function CustomerNotificationsSection() {
  // The account's own operator flag, not the view-as adjusted claims (Watch content is refused to it either way),
  // unless the account holds onguard-entraid-admin on this very customer (ENTRAID-302).
  const me = useOutletContext()?.me;
  return (
    <RecordSection
      id="notifications"
      ownHeader
      render={(r) => (
        <NotificationsSection
          customerId={r.customerId} config={r.config} isOperator={r.isOperator} operatorAccount={watchOperatorAccount(me, r.customerId)} onConfigSaved={r.refresh}
          notice={<LifecycleNotice customer={r.customer} audience={r.audience} hasData onRefresh={r.refresh} />}
        />
      )}
    />
  );
}

/**
 * /customers/:id/activity/events/:eventId (spec 9.3, 9.4; /notifications/events/:eventId before the ENTRAID-347
 * redesign, still redirected for every email already sent): the admin's event view and every recipient's deep link.
 * It skips the sidebar check on purpose: a developer-only or qlik-credentials-only recipient has no Activity item but
 * may open their own notification; the portal answers 404 to anyone who is neither the recipient nor an admin. The
 * account's own operator flag (not the view-as claims) keeps operators out: the portal refuses them Watch content even
 * while they view as a client.
 */
export function WatchEventSection() {
  const ctx = useOutletContext();
  const operatorAccount = watchOperatorAccount(ctx?.me, ctx?.customerId);
  const isAdmin = readsWatch(ctx);
  return (
    <>
      <PageHeader
        title="Activity event"
        back={isAdmin ? { to: activityPath(ctx.customerId), label: 'Activity' } : null}
      />
      <WatchEventPage customerId={ctx.customerId} isAdmin={isAdmin} operatorAccount={operatorAccount} />
    </>
  );
}

/** The pre-Watch URL /customers/:id/alerts (workbench, 2026-09-29): the rules. */
function AlertsRedirect() {
  const { customerId } = useParams();
  return <Navigate to={notificationsPath(customerId, 'rules')} replace />;
}

/**
 * Watch URLs before Activity had its own section (ENTRAID-347 plan C3), kept for every email and bookmark already out:
 *   /notifications                     the old Overview feed (the daily digest's "see more" link)  -> /activity
 *   /notifications/events/:eventId     every Watch email's event link, with its ?n= notification   -> /activity/events/:eventId
 *   /notifications/flagged[/:caseId]   the Flagged tab and case links                               -> /activity/flagged[/:caseId]
 *   /notifications/cases[/:caseId]     the name before Flagged                                      -> /activity/flagged[/:caseId]
 * The query string and hash are kept. No sidebar check here: the target applies its own (an event page none).
 */
function WatchMovedRedirect({ to }) {
  const { customerId, eventId, caseId } = useParams();
  const { search, hash } = useLocation();
  const sub = to === 'event' ? `events/${encodeURIComponent(eventId)}` : to === 'case' ? `flagged/${encodeURIComponent(caseId)}` : to;
  return <Navigate to={`${activityPath(customerId, sub)}${search}${hash}`} replace />;
}

/**
 * Compliance (ENTRAID-307). The tier comes from the customer record, which an auditor cannot read: for them the tier
 * is unknown here and the API's own tier answer applies. Operators and admins wait for the record.
 */
export function ComplianceRouteSection() {
  const ctx = useOutletContext();
  const { redirect } = useSectionAccess('reports');
  if (redirect) return <Navigate to={redirect} replace />;
  const role = complianceRole(ctx.me, ctx.navClaims, ctx.customerId);
  if (!role) return <Navigate to={sectionTarget(ctx.navClaims, ctx.customerId, 'overview')} replace />;
  const needsRecord = ctx.fullAccess === true;
  if (needsRecord && (ctx.recordError || !ctx.record)) return <RecordPending id="reports" error={ctx.recordError} />;
  return (
    <>
    <SectionHeader id="reports" />
    <ComplianceSection
      customerId={ctx.customerId}
      role={role}
      tier={ctx.record?.customer?.tierName}
      tierKnown={needsRecord}
      customer={ctx.record?.customer ?? null}
      marketplaceListingUrl={ctx.me?.config?.marketplaceListingUrl ?? ''}
      userEmail={ctx.me?.user?.email ?? ''}
    />
    </>
  );
}

export function PeopleSection() {
  return (
    <PlainSection
      id="people"
      render={(ctx) => (
        <CustomerPeople
          customerId={ctx.customerId}
          isOperator={ctx.navClaims.isOperator === true}
          operatorAccount={watchOperatorAccount(ctx.me, ctx.customerId)}
        />
      )}
    />
  );
}

/**
 * Settings (ENTRAID-347 redesign, plan C6): Company Settings (Branding) and Deployment on one page, same access as both
 * had (operator or the customer's admin). Branding is read-only for an operator account that is not this tenant's admin
 * (the account's own flag, so also in "View as client"); the operator controls and the Danger zone follow the
 * navigation claim, as on Deployment before.
 */
export function SettingsSection() {
  const me = useOutletContext()?.me;
  return (
    <RecordSection
      id="settings"
      ownHeader
      render={(r) => <SettingsPage customer={r.customer} isOperator={r.isOperator} operatorAccount={operatorWithoutAdmin(me, r.customerId)} onRefresh={r.refresh} />}
    />
  );
}

/** The Company Settings and Deployment URLs before Settings (lib/legacy-routes.js legacySettingsTarget). */
function SettingsMovedRedirect({ from }) {
  const ctx = useOutletContext();
  const { search } = useLocation();
  return <Navigate to={legacySettingsSectionTarget(ctx.navClaims, ctx.customerId, from, search)} replace />;
}

export function CustomerDataReferenceSection() {
  // DataReference draws its own h1 page title.
  return <PlainSection id="data-reference" ownHeader render={() => <DataReference />} />;
}

/** Implementation footprint (spec 2026-10-29): draws its own h1. An operator may open it but not the PDF. */
export function ImplementationFootprintSection() {
  // Every caller of this page may request the PDF, IPC Global operators included (Hugo Andrade, 2026-10-30). The Data
  // reference links show only to callers who can open Data reference (an auditor alone cannot). Display only.
  return (
    <PlainSection
      id="implementation-footprint"
      ownHeader
      render={(ctx) => (
        <ImplementationFootprint
          customerId={ctx.customerId}
          canOpenDataReference={canSee(ctx.navClaims, customerScope(ctx.customerId), 'data-reference')}
        />
      )}
    />
  );
}

const SECTION_ELEMENTS = Object.freeze({
  overview: OverviewSection,
  security: SecuritySection,
  licenses: LicensesSection,
  'access-reviews': AccessReviewsSection,
  activity: ActivitySection,
  reports: ComplianceRouteSection,
  'data-collection': DataCollectionSection,
  'qlik-cloud': QlikCloudSection,
  notifications: CustomerNotificationsSection,
  people: PeopleSection,
  settings: SettingsSection,
  'implementation-footprint': ImplementationFootprintSection,
  'data-reference': CustomerDataReferenceSection,
});

// Sections with their own nested routes (tabs addressed by the path).
const NESTED = new Set(['access-reviews', 'activity', 'notifications', 'people', 'reports', 'settings']);

/** The child routes of customers/:customerId, used by App.jsx and the tests. */
export function customerSectionRoutes() {
  return [
    <Route key="index" index element={<LegacyCustomerRedirect />} />,
    <Route key="watch-event" path="activity/events/:eventId" element={<WatchEventSection />} />,
    <Route key="old-watch-feed" path="notifications" element={<WatchMovedRedirect to="" />} />,
    <Route key="old-watch-event" path="notifications/events/:eventId" element={<WatchMovedRedirect to="event" />} />,
    <Route key="old-flagged" path="notifications/flagged" element={<WatchMovedRedirect to="flagged" />} />,
    <Route key="old-flagged-case" path="notifications/flagged/:caseId" element={<WatchMovedRedirect to="case" />} />,
    <Route key="old-cases" path="notifications/cases" element={<WatchMovedRedirect to="flagged" />} />,
    <Route key="old-cases-case" path="notifications/cases/:caseId" element={<WatchMovedRedirect to="case" />} />,
    ...CUSTOMER_SECTIONS.map((id) => (
      <Route key={id} path={NESTED.has(id) ? `${id}/*` : id} element={createElement(SECTION_ELEMENTS[id])} />
    )),
    <Route key="alerts" path="alerts/*" element={<AlertsRedirect />} />,
    // Renamed 2026-09-30: the old section routes keep working.
    ...Object.keys(LEGACY_SECTIONS).map((old) => <Route key={old} path={old} element={<LegacySectionRedirect />} />),
    <Route key="legacy-compliance" path="compliance/*" element={<LegacyComplianceRedirect />} />,
    <Route key="old-company-settings" path="company-settings/*" element={<SettingsMovedRedirect from="company-settings" />} />,
    <Route key="old-deployment" path="deployment/*" element={<SettingsMovedRedirect from="deployment" />} />,
    <Route key="unknown" path="*" element={<LegacyCustomerRedirect />} />,
  ];
}
